import { useEffect, useState } from 'react';
import { ERROR_NOTIFICATION_DURATION_MS } from '../config/constants';

export type NotificationType = 'error' | 'success' | 'warning' | 'info';

interface Notification {
    id: string;
    message: string;
    type: NotificationType;
    timestamp: string;
}

/**
 * Toast notification component for displaying notifications to users
 * Listens for app-notification events and displays them temporarily
 */
export const NotificationContainer = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    useEffect(() => {
        const handleNotification = (event: Event) => {
            const customEvent = event as CustomEvent<{ 
                message: string; 
                type: NotificationType;
                timestamp: string;
            }>;
            const notification: Notification = {
                id: `${Date.now()}-${Math.random()}`,
                message: customEvent.detail.message,
                type: customEvent.detail.type,
                timestamp: customEvent.detail.timestamp
            };

            setNotifications(prev => [...prev, notification]);

            // Auto-dismiss after configured duration
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
            }, ERROR_NOTIFICATION_DURATION_MS);
        };

        const handleError = (event: Event) => {
            const customEvent = event as CustomEvent<{ message: string; timestamp: string }>;
            const notification: Notification = {
                id: `${Date.now()}-${Math.random()}`,
                message: customEvent.detail.message,
                type: 'error',
                timestamp: customEvent.detail.timestamp
            };

            setNotifications(prev => [...prev, notification]);

            // Auto-dismiss after configured duration
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
            }, ERROR_NOTIFICATION_DURATION_MS);
        };

        window.addEventListener('app-notification', handleNotification);
        // Keep backward compatibility with app-error events
        window.addEventListener('app-error', handleError);

        return () => {
            window.removeEventListener('app-notification', handleNotification);
            window.removeEventListener('app-error', handleError);
        };
    }, []);

    if (notifications.length === 0) {
        return null;
    }

    const getNotificationStyles = (type: NotificationType) => {
        switch (type) {
            case 'error':
                return {
                    backgroundColor: 'var(--color-error-bg)',
                    border: '1px solid var(--color-error-border)',
                    color: 'var(--color-error-critical)',
                    iconColor: 'var(--color-error-critical)'
                };
            case 'success':
                return {
                    backgroundColor: 'var(--color-success-bg)',
                    border: '1px solid var(--color-success-border)',
                    color: 'var(--color-success)',
                    iconColor: 'var(--color-success)'
                };
            case 'warning':
                return {
                    backgroundColor: 'var(--color-warning-bg)',
                    border: '1px solid var(--color-warning-border)',
                    color: 'var(--color-warning)',
                    iconColor: 'var(--color-warning)'
                };
            case 'info':
                return {
                    backgroundColor: 'var(--color-info-bg)',
                    border: '1px solid var(--color-info-border)',
                    color: 'var(--color-info)',
                    iconColor: 'var(--color-info)'
                };
        }
    };

    const getIcon = (type: NotificationType) => {
        switch (type) {
            case 'error':
                return (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                        <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" />
                    </svg>
                );
            case 'success':
                return (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                        <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.293 7.293l-5 5a1 1 0 01-1.414 0l-2.5-2.5a1 1 0 111.414-1.414L9 10.586l4.293-4.293a1 1 0 111.414 1.414z" />
                    </svg>
                );
            case 'warning':
                return (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                        <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm1 14H9v-2h2v2zm0-4H9V6h2v4z" />
                    </svg>
                );
            case 'info':
                return (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                        <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm1 15H9v-4h2v4zm0-6H9V5h2v4z" />
                    </svg>
                );
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            maxWidth: '400px'
        }}>
            {notifications.map(notification => {
                const styles = getNotificationStyles(notification.type);
                return (
                    <div
                        key={notification.id}
                        style={{
                            padding: '1rem',
                            backgroundColor: styles.backgroundColor,
                            border: styles.border,
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem',
                            animation: 'slideIn 0.3s ease-out'
                        }}
                    >
                        <div style={{ color: styles.iconColor }}>
                            {getIcon(notification.type)}
                        </div>
                        <div style={{ flex: 1 }}>
                            <p style={{ 
                                margin: 0, 
                                color: styles.color, 
                                fontSize: '0.875rem', 
                                fontWeight: 500 
                            }}>
                                {notification.message}
                            </p>
                        </div>
                        <button
                            onClick={() => {
                                setNotifications(prev => prev.filter(n => n.id !== notification.id));
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0',
                                color: 'var(--color-text-tertiary)',
                                fontSize: '1.25rem',
                                lineHeight: 1,
                                flexShrink: 0
                            }}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    </div>
                );
            })}
            <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

